import { useMemo, useState, type ReactNode } from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import Select, {
	components,
	type ControlProps,
	type OptionProps,
} from 'react-select';
import {
	BsCheck,
	BsFuelPump,
	BsFunnel,
	BsLightningCharge,
	BsPlug,
} from 'react-icons/bs';
import { RiLeafLine } from 'react-icons/ri';
import Fuse from 'fuse.js';
import Skeleton from 'react-loading-skeleton';
import { StellantisRequestQuoteModal } from 'src/components/Stellantis';
import { useStellantisActions } from 'src/store/StellantisStore';
import ToolTip from 'src/components/Tooltip/Tooltip';
import { RoleEnum } from 'src/api';
import { useAuth } from 'src/hooks/useAuth';
import { useModels } from '../api';
import type { Model } from '../domain';
import { useConfiguratorState } from '../store';
import {
	BackLink,
	ConfiguratorSearchInput,
	formatCurrency,
	QueryStateBoundary,
} from '../common';
import { useConfiguratorTranslations } from '../translations';
import { resetAutoSelectedTrim } from './TrimSelector';
import { resetAutoSelectedStock } from './StockSelector';
import styles from './ModelSelector.module.scss';
import shared from '../configurator.module.scss';

const ALL_CHIP = 'Tutti';

function useModelFilters(models: Model[]) {
	const chips = useMemo(() => {
		const present = new Set<string>();
		for (const m of models) {
			const value = m.bodyType?.trim();
			if (value) present.add(value);
		}
		return [ALL_CHIP, ...[...present].sort((a, b) => a.localeCompare(b))];
	}, [models]);

	const [activeChip, setActiveChip] = useState<string>(ALL_CHIP);
	const effectiveChip = chips.includes(activeChip) ? activeChip : ALL_CHIP;

	return { chips, activeChip: effectiveChip, setActiveChip };
}

export type SortKey = 'price-asc' | 'price-desc';

function useModelSort() {
	const [sortKey, setSortKey] = useState<SortKey>('price-asc');
	const tr = useConfiguratorTranslations();

	const options = useMemo(
		() => [
			{ value: 'price-asc' as SortKey, label: tr.sortPriceAsc },
			{ value: 'price-desc' as SortKey, label: tr.sortPriceDesc },
		],
		[tr.sortPriceAsc, tr.sortPriceDesc]
	);

	return { sortKey, setSortKey, options };
}

function useFilteredModels(
	models: Model[],
	activeChip: string,
	sortKey: SortKey,
	query: string
): Model[] {
	const fuse = useMemo(
		() =>
			new Fuse(models, { keys: ['make', 'model', 'bodyType'], threshold: 0.3 }),
		[models]
	);

	return useMemo(() => {
		const searched =
			query.trim() === '' ? models : fuse.search(query).map((r) => r.item);

		const byChip =
			activeChip === ALL_CHIP
				? searched
				: searched.filter((m) =>
					m.bodyType?.toLowerCase().includes(activeChip.toLowerCase())
				);

		return [...byChip].sort((a, b) => {
			const ap = Number(a.minPrice);
			const bp = Number(b.minPrice);
			return sortKey === 'price-asc' ? ap - bp : bp - ap;
		});
	}, [models, activeChip, sortKey, query, fuse]);
}

function ModelFilters({
	chips,
	active,
	onChange,
}: {
	chips: string[];
	active: string;
	onChange: (c: string) => void;
}) {
	const tr = useConfiguratorTranslations();
	return (
		<div className={shared.chipRow}>
			{chips.map((c) => (
				<button
					key={c}
					className={`${shared.chip} ${c === active ? shared.chipActive : ''}`}
					onClick={() => onChange(c)}
				>
					{tr.bodyTypeLabel(c)}
				</button>
			))}
		</div>
	);
}

interface Opt {
	value: SortKey;
	label: string;
}

function Option({ children, ...props }: OptionProps<Opt, false>) {
	return (
		<components.Option {...props}>
			<span
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
				}}
			>
				<span>{children}</span>
				{props.isSelected && <BsCheck size={16} />}
			</span>
		</components.Option>
	);
}

function Control({ children, ...props }: ControlProps<Opt, false>) {
	return (
		<components.Control {...props}>
			<BsFunnel
				style={{ marginLeft: '0.25rem', color: '#6c757d', flexShrink: 0 }}
			/>
			{children}
		</components.Control>
	);
}

const selectStyles = {
	control: (base: object) => ({
		...base,
		border: 'none',
		boxShadow: 'none',
		background: 'transparent',
		cursor: 'pointer',
		minHeight: 'unset',
		flexWrap: 'nowrap' as const,
	}),
	valueContainer: (base: object) => ({
		...base,
		padding: '0 0.25rem',
		flexWrap: 'nowrap' as const,
	}),
	singleValue: (base: object) => ({
		...base,
		color: '#6c757d',
		fontSize: '0.875rem',
		whiteSpace: 'nowrap' as const,
	}),
	placeholder: (base: object) => ({
		...base,
		color: '#6c757d',
		fontSize: '0.875rem',
	}),
	dropdownIndicator: (base: object) => ({
		...base,
		color: '#6c757d',
		padding: '0 0.25rem',
	}),
	indicatorSeparator: () => ({ display: 'none' }),
	menu: (base: object) => ({
		...base,
		fontSize: '0.875rem',
		minWidth: '180px',
		right: 0,
		left: 'auto',
	}),
	option: (
		base: object,
		state: { isSelected: boolean; isFocused: boolean }
	) => ({
		...base,
		backgroundColor: state.isFocused && !state.isSelected ? '#061128' : 'white',
		color: state.isFocused && !state.isSelected ? 'white' : '#061128',
		cursor: 'pointer',
	}),
};

function ModelSortSelect({
	value,
	onChange,
	options,
}: {
	value: SortKey;
	onChange: (k: SortKey) => void;
	options: Opt[];
}) {
	const tr = useConfiguratorTranslations();
	const sel = options.find((o) => o.value === value);

	return (
		<Select<Opt>
			options={options}
			value={sel}
			onChange={(o) => o && onChange(o.value)}
			isSearchable={false}
			styles={selectStyles}
			components={{ Control, Option }}
			formatOptionLabel={(opt, { context }) =>
				context === 'value' ? tr.sortLabel(opt.label) : opt.label
			}
		/>
	);
}

const FUEL_ICON: Record<string, ReactNode> = {
	BENZ: <BsFuelPump />,
	DIES: <BsFuelPump />,
	MHEV: <RiLeafLine />,
	IBRIP: <RiLeafLine />,
	PHEV: <BsPlug />,
	ELEC: <BsLightningCharge />,
};

function ModelCard({ model }: { model: Model }) {
	const displayName = `${model.make} ${model.model}`;
	const setModel = useConfiguratorState((s) => s.setModel);

	const onConfigure = () => {
		resetAutoSelectedTrim();
		resetAutoSelectedStock();
		setModel(model.modelCode, model.model);
	};
	const tr = useConfiguratorTranslations();
	const [imgFailed, setImgFailed] = useState(false);
	const carPlaceholder = `${process.env.PUBLIC_URL}/images/placeholder.png`;
	const imgSrc = !model.img || imgFailed ? carPlaceholder : model.img;

	return (
		<Card
			className={`h-100 ${styles.modelCard}`}
			onClick={onConfigure}
			role="button"
			tabIndex={0}
			onKeyDown={(e) => e.key === 'Enter' && onConfigure()}
		>
			<Card.Img
				variant="top"
				className={styles.modelCardImage}
				src={imgSrc}
				alt={displayName}
				onError={() => setImgFailed(true)}
			/>
			<Card.Body>
				<Card.Title as="div" className="fw-semibold text-dark mb-1">
					{displayName}
				</Card.Title>
				{model.bodyType && (
					<div className={styles.modelBodyType}>{model.bodyType}</div>
				)}
				{model.fuels.length > 0 && (
					<div className={styles.modelFuels}>
						<span className={styles.modelFuelItem}>
							{FUEL_ICON[model.fuels[0]] ?? <BsFuelPump />}
							{tr.fuelCodeLabel(model.fuels[0])}
						</span>
						{model.fuels.length > 1 && (
							<>
								<span
									id={`mn-fuel-more-${model.make}-${model.model}`.replace(
										/\s+/g,
										'-'
									)}
									className={styles.modelFuelMore}
								>
									+{model.fuels.length - 1}
								</span>
								<ToolTip
									anchorId={`mn-fuel-more-${model.make}-${model.model}`.replace(
										/\s+/g,
										'-'
									)}
									place="top"
									clickable
								>
									<ul className={styles.modelFuelTooltip}>
										{model.fuels.slice(1).map((f) => (
											<li key={f}>{tr.fuelCodeLabel(f)}</li>
										))}
									</ul>
								</ToolTip>
							</>
						)}
					</div>
				)}
				{model.minPrice > 0 && (
					<div className={styles.modelPrice}>
						{tr.modelFromPrice}{' '}
						<strong>{formatCurrency(Number(model.minPrice))}</strong>
					</div>
				)}
			</Card.Body>
		</Card>
	);
}

function ModelGrid({ models }: { models: Model[] }) {
	const tr = useConfiguratorTranslations();

	if (models.length === 0) {
		return <div className="text-muted">{tr.modelEmpty}</div>;
	}

	return (
		<Row className="g-4">
			{models.map((m) => (
				<Col key={m.modelCode} xs={12} md={6} lg={4}>
					<ModelCard model={m} />
				</Col>
			))}
		</Row>
	);
}

function ModelGridSkeleton() {
	return (
		<Row className="g-4">
			{Array.from({ length: 3 }).map((_, i) => (
				<Col key={i} xs={12} md={6} lg={4}>
					<Skeleton style={{ aspectRatio: '16/9', display: 'block' }} />
					<div className="mt-2">
						<Skeleton width="55%" height={18} />
						<div className="mt-1">
							<Skeleton width="30%" height={12} />
						</div>
						<div className="mt-2">
							<Skeleton width="70%" height={12} />
						</div>
					</div>
				</Col>
			))}
		</Row>
	);
}

function ModelGridContent({
	models,
	activeChip,
	sortKey,
	search,
}: {
	models: Model[];
	activeChip: string;
	sortKey: SortKey;
	search: string;
}) {
	const filtered = useFilteredModels(models, activeChip, sortKey, search);
	return <ModelGrid models={filtered} />;
}

export function ModelSelector() {
	const { userRole } = useAuth();
	const makeCode = useConfiguratorState((s) => s.makeCode);
	const goToStep = useConfiguratorState((s) => s.goToStep);
	const query = useModels(makeCode);
	const filters = useModelFilters(query.data ?? []);
	const sort = useModelSort();
	const [search, setSearch] = useState('');
	const tr = useConfiguratorTranslations();
	const { openRequestQuoteModal } = useStellantisActions();
	const canCreateQuoteRequest = [
		RoleEnum.SALES,
		RoleEnum.SALES_DEALER,
		RoleEnum.SEGNALATORE,
	].includes(userRole);

	return (
		<>
			<section>
				<BackLink onClick={() => goToStep('brand-selector')}>
					{tr.backToBrands}
				</BackLink>

				<div className={styles.modelHeaderRow}>
					<div>
						<h1 className={styles.modelHeading}>{tr.modelHeading}</h1>
						<p className={styles.modelSubtitle}>{tr.modelSubtitle}</p>
					</div>
					<div className={styles.modelSearchWrap}>
						<ConfiguratorSearchInput
							value={search}
							onChange={setSearch}
							placeholder={tr.modelSearchPlaceholder}
						/>
					</div>
				</div>

				<div className={styles.filtersRow}>
					<ModelFilters
						chips={filters.chips}
						active={filters.activeChip}
						onChange={filters.setActiveChip}
					/>
					<div className={styles.sortWrap}>
						<ModelSortSelect
							value={sort.sortKey}
							onChange={sort.setSortKey}
							options={sort.options}
						/>
					</div>
				</div>

				<hr className={styles.divider} />

				<QueryStateBoundary query={query} skeleton={<ModelGridSkeleton />}>
					{(models) => (
						<ModelGridContent
							models={models}
							activeChip={filters.activeChip}
							sortKey={sort.sortKey}
							search={search}
						/>
					)}
				</QueryStateBoundary>

				{canCreateQuoteRequest && query.isSuccess && (
					<div className={styles.ctaCard}>
						<div>
							<p className={styles.ctaTitle}>{tr.ctaTitle}</p>
							<p className={styles.ctaSubtitle}>{tr.ctaSubtitle}</p>
						</div>
						<button
							className={styles.ctaButton}
							onClick={() => openRequestQuoteModal(undefined, 'configuratore')}
						>
							{tr.ctaButton}
						</button>
					</div>
				)}
			</section>
			{canCreateQuoteRequest && <StellantisRequestQuoteModal />}
		</>
	);
}
